/**
 * PushPrompt.jsx — src/components/PushPrompt.jsx
 * Проактивное предложение подключить уведомления.
 * Показывается всем сотрудникам, если push не подключены на этом устройстве.
 */
import { useContext, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Button, IconButton, Sheet, Stack, Typography } from '@mui/joy';

import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';

import { LayoutContext } from '../context';
import { APIGetPushStatus } from '../api';

const SNOOZE_KEY = 'push_prompt_snoozed_until';
const SNOOZE_LATER_MS = 24 * 60 * 60 * 1000;      // «Позже» — сутки
const SNOOZE_CLOSE_MS = 3 * 24 * 60 * 60 * 1000;  // крестик — трое суток
const SHOW_DELAY_MS = 4000;                        // не выпрыгивать сразу при входе

const isSnoozed = () => {
    try {
        const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
        return until > Date.now();
    } catch { return false; }
};

const snooze = (ms) => {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + ms)); } catch {}
};

const detectIOS = () => {
    const ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua)
        || (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua));
};

const isStandalonePWA = () =>
    window.navigator?.standalone === true
    || window.matchMedia?.('(display-mode: standalone)')?.matches === true;


export default function PushPrompt() {
    const { account, tokenEmployee, subscribeUserToPush, newsStories } = useContext(LayoutContext);

    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [variant, setVariant] = useState('enable'); // enable | ios_install | denied

    useEffect(() => {
        if (!account?.id || !tokenEmployee) return;
        if (isSnoozed()) return;
        if (typeof Notification === 'undefined' || !('Notification' in window)) return;
        if (!('serviceWorker' in navigator)) return;

        let alive = true;

        const check = async () => {
            const permission = Notification.permission;

            // Разрешение есть — уточняем у сервера, доехала ли подписка с этого устройства
            if (permission === 'granted') {
                let deviceToken = null;
                try { deviceToken = localStorage.getItem('push_device_token'); } catch {}
                const status = await APIGetPushStatus(tokenEmployee, deviceToken);
                if (!alive) return;

                // has_active === null → старый сервер, статус неизвестен, не пристаём
                if (status.has_active === null) return;
                if (status.this_device) return;

                setVariant('enable');
                setOpen(true);
                return;
            }

            if (permission === 'denied') {
                setVariant('denied');
                setOpen(true);
                return;
            }

            // permission === 'default'
            if (detectIOS() && !isStandalonePWA()) {
                setVariant('ios_install');
                setOpen(true);
                return;
            }

            setVariant('enable');
            setOpen(true);
        };

        const timer = setTimeout(check, SHOW_DELAY_MS);
        return () => { alive = false; clearTimeout(timer); };
    }, [account?.id, tokenEmployee]);

    const handleEnable = async () => {
        setBusy(true);
        const result = await subscribeUserToPush(tokenEmployee);
        setBusy(false);

        if (result?.type === 'success') {
            setOpen(false);
            return;
        }
        if (result?.type === 'ios_need_install') {
            setVariant('ios_install');
            return;
        }
        if (result?.type?.startsWith('permission_denied')) {
            setVariant('denied');
            return;
        }
        // Прочие сбои — откладываем, чтобы не мозолить глаза
        snooze(SNOOZE_LATER_MS);
        setOpen(false);
    };

    const handleLater = () => { snooze(SNOOZE_LATER_MS); setOpen(false); };
    const handleClose = () => { snooze(SNOOZE_CLOSE_MS); setOpen(false); };

    // Не спорим со сторис новостей
    if (newsStories?.open) return null;

    const texts = {
        enable: {
            title: 'Включите уведомления',
            body: 'Мы сообщим о новых замерах, изменениях времени и сообщениях в чате — даже когда сайт закрыт.',
            action: 'Включить',
        },
        ios_install: {
            title: 'Уведомления на iPhone',
            body: 'Чтобы получать уведомления, добавьте сайт на экран «Домой»: кнопка «Поделиться» внизу Safari → «На экран Домой». Затем откройте приложение с домашнего экрана.',
            action: 'Понятно',
        },
        denied: {
            title: 'Уведомления отключены',
            body: 'Разрешение на уведомления заблокировано в браузере. Включите его в настройках сайта: значок замка в адресной строке → «Уведомления» → «Разрешить».',
            action: 'Понятно',
        },
    }[variant];

    return (
        <AnimatePresence>
            {open && (
                <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 26 }}
                    sx={{
                        position: 'fixed',
                        zIndex: 1400,
                        left: { xs: 12, sm: 'auto' },
                        right: { xs: 12, sm: 24 },
                        bottom: { xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 24 },
                        width: { xs: 'auto', sm: 380 },
                    }}
                >
                    <Sheet sx={{
                        borderRadius: '18px',
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 2,
                        boxShadow: '0 18px 48px rgba(15,23,42,0.22)',
                        bgcolor: 'background.surface',
                    }}>
                        <Stack direction="row" spacing={1.4} alignItems="flex-start">
                            <Box sx={{
                                width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                                background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {variant === 'ios_install'
                                    ? <IosShareRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />
                                    : <NotificationsActiveRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 0.4 }}>
                                    {texts.title}
                                </Typography>
                                <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                                    {texts.body}
                                </Typography>

                                <Stack direction="row" spacing={1} sx={{ mt: 1.4 }}>
                                    {variant === 'enable' ? (
                                        <>
                                            <Button size="sm" loading={busy} onClick={handleEnable} sx={{ fontWeight: 700 }}>
                                                {texts.action}
                                            </Button>
                                            <Button size="sm" variant="plain" color="neutral" onClick={handleLater}>
                                                Позже
                                            </Button>
                                        </>
                                    ) : (
                                        <Button size="sm" variant="soft" onClick={handleClose} sx={{ fontWeight: 700 }}>
                                            {texts.action}
                                        </Button>
                                    )}
                                </Stack>
                            </Box>

                            <IconButton size="sm" variant="plain" color="neutral" onClick={handleClose}>
                                <CloseRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Stack>
                    </Sheet>
                </Box>
            )}
        </AnimatePresence>
    );
}